import React from 'react'

import { BillingPlan, seatRate } from '#components/Settings/Org/plans'
import { useOrgBillingPlan } from '#components/Settings/Org/useBillingPlan'
import { IOrg } from '#state'
import { formatMoney } from '#util'

import { useSeatCapacity } from './useSeatCapacity'
import { SeatClaim } from './useSeatChanges'

type AnnualSeatStep = 'free' | 'keep' | 'buy'

const annualSeatStep = (
  plan: BillingPlan | null | undefined,
  taken: number | null | undefined,
  holdsSeat: boolean,
): AnnualSeatStep | null => {
  if (plan?.seatMode !== 'static' || !plan.seats || typeof taken !== 'number') {
    return null
  }
  if (holdsSeat) return 'free'
  const seatsAtRenewal = plan.scheduledSeats ?? plan.seats
  if (taken < seatsAtRenewal) return 'free'
  return seatsAtRenewal < plan.seats ? 'keep' : 'buy'
}

type Notice = {
  color: string
  text: React.ReactNode
  detail?: string
}

const buyNotice = (
  plan: BillingPlan | null | undefined,
  needed: number,
): Notice => ({
  color: 'yellow',
  text: plan ? (
    <>
      Inviting a new member will increase your subscription cost by{' '}
      <strong>
        {formatMoney(plan.unitAmount * needed, plan.currency, {
          trailingZeroDisplay: 'stripIfInteger',
        })}
      </strong>
      /year.
    </>
  ) : (
    'Inviting a new member will increase your subscription cost.'
  ),
  detail:
    plan?.status === 'trialing'
      ? 'You will be charged when your trial ends.'
      : `You will immediately be charged the prorated amount for this year's term.`,
})

const billingNotice = (
  plan: BillingPlan | null | undefined,
  holdsSeat: boolean,
  step: AnnualSeatStep | null,
  emptySeat: boolean,
): Notice | null => {
  if (!plan?.seats) return null
  if (plan.seatMode === 'static') {
    switch (step) {
      case 'buy':
        return buyNotice(plan, 1)
      case 'keep':
        return {
          color: 'gray',
          text: 'This member will occupy a seat that was previously scheduled to be removed. There will be no additional charge.',
        }
      case 'free':
      case null:
        return {
          color: 'gray',
          text: holdsSeat
            ? 'This member already holds a seat on your subscription, so there is no additional charge.'
            : 'Your workspace has empty seats, so you can invite a new member for no additional charge.',
        }
    }
  }
  if (holdsSeat) {
    return {
      color: 'gray',
      text: 'This member already holds a seat on your subscription, so there is no additional charge.',
    }
  }
  if (emptySeat) {
    return {
      color: 'gray',
      text: 'Your workspace has empty seats, so you can invite a new member for no additional charge.',
    }
  }
  if (plan.promotional) return null
  const rate = seatRate(plan)
  return {
    color: 'yellow',
    text: rate
      ? `Adding a new member will increase your subscription cost by ${rate}.`
      : 'Adding a new member will increase your subscription cost.',
  }
}

export const useInviteSeat = (org: IOrg, email: string) => {
  const plan = useOrgBillingPlan(org).data
  const capacity = useSeatCapacity(org).data
  const [seatsNeeded, setSeatsNeeded] = React.useState<{
    email: string
    count: number
  } | null>(null)
  const needed = seatsNeeded?.email === email ? seatsNeeded.count : null
  const setNeeded = (count: number) => setSeatsNeeded({ email, count })
  const holdsSeat = (capacity?.freeInvitees ?? []).some(
    (person) => person.email.toLowerCase() === email.toLowerCase(),
  )
  const step = annualSeatStep(plan, capacity?.taken, holdsSeat)
  const emptySeat =
    plan?.seatMode === 'dynamic' &&
    !!plan.seats &&
    typeof capacity?.headcount === 'number' &&
    capacity.headcount < plan.seats
  const toBuy = needed ?? (step === 'buy' || step === 'keep' ? 1 : null)
  const notice = needed
    ? buyNotice(plan, needed)
    : billingNotice(plan, holdsSeat, step, emptySeat)

  const claim: SeatClaim | undefined =
    toBuy && plan?.seats
      ? { quantity: (plan.scheduledSeats ?? plan.seats) + toBuy }
      : undefined
  return { notice, claim, setNeeded }
}
